import { css } from '@emotion/react';
import {
  Device,
  DeviceType_Enum,
  Provision,
  ProvisionAuthentication_Enum,
  ProvisionHealth_Enum,
} from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { useQueryClient } from 'react-query';
import { DevicesApi, DevicesMutations, DevicesQueryKey, DevicesTypes } from '@frontend/api-devices';
import { SipProfilesApiTypes } from '@frontend/api-sip-profile';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useMutation } from '@frontend/react-query-helpers';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  Button,
  Chip,
  ChipOptional,
  ConfirmationModal,
  SwitchField,
  Table,
  Text,
  TextLink,
  useAlert,
  useControlledField,
  useModalControl,
  useTooltip,
} from '@frontend/design-system';
import { trackingId } from '../../../tracking';
import { logError } from '../../../utils/loggers';

const CONTAINER_BREAKPOINT = 680;

const WIFI_CAPABLE_MODELS = ['t54w', 't85w', 't53w', 't57w', 't58w', 't87w'];

type Props = {
  device: Device | undefined;
  registration: SipProfilesApiTypes.SipProfileType['registration'] | undefined;
};
export const DeviceSettingsIdSummary = ({ device, registration }: Props) => {
  const { t } = useTranslation('phone', { keyPrefix: 'devices' });
  const { navigate } = useSettingsNavigate();

  const data = {
    ...device,
    ...registration,
  };

  return (
    <div
      css={css`
        display: grid;
        gap: ${theme.spacing(3)};
        grid-template-columns: minmax(0, 1fr) 184px;
        margin-bottom: ${theme.spacing(4)};
      `}
    >
      <Table
        emptyStateConfig={{
          type: 'sync_your_phone',
        }}
        colConfig={[
          {
            Header: t('MAC Address'),
            accessor: ({ macAddress }) => (macAddress ? macAddress : '—'),
            disableSortBy: true,
            id: 'macAddress',
          },
          {
            Header: t('Make & Model'),
            accessor: ({ make, model, type }) =>
              type === DeviceType_Enum.SOFTPHONE
                ? 'Weave'
                : make || model
                ? make + ' ' + model?.toLocaleUpperCase()
                : '—',
            disableSortBy: true,
            id: 'make',
          },
          {
            Header: t('Allow Wi-Fi'),
            accessor: (deviceData) => deviceData,
            cellRenderer: (deviceData: typeof data) => (
              <WifiAllowedToggle device={device} wifiAllowed={deviceData.wifiAllowed} />
            ),
            omit: !WIFI_CAPABLE_MODELS.includes(device?.model ?? ''),
            disableSortBy: true,
            id: 'allowWifi',
            width: 150,
          },
          {
            Header: t('Registration Status'),
            accessor: ({ active }) => active ?? false,
            cellRenderer: (registrationStatus) => (
              <Text color={!!registrationStatus ? 'success' : 'error'}>
                {!!registrationStatus ? (
                  <Chip variant='success'>{t('Registered')}</Chip>
                ) : (
                  <Chip variant='critical'>{t('Not Registered')}</Chip>
                )}
              </Text>
            ),
            disableSortBy: true,
            id: 'registration',
          },
          {
            Header: () => (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  width: 100%;
                  gap: ${theme.spacing(3)};

                  @container (max-width: ${CONTAINER_BREAKPOINT}px) {
                    flex-direction: column;
                    gap: 0;
                    align-items: flex-start;
                  }
                `}
              >
                <Text
                  size='medium'
                  as='span'
                  css={css`
                    color: ${theme.colors.neutral40};

                    @container (max-width: ${CONTAINER_BREAKPOINT}px) {
                      font-size: ${theme.fontSize(12)};
                      color: ${theme.colors.neutral70};
                    }
                  `}
                >
                  {t('Provisioning Status')}
                </Text>
                <TextLink
                  rel='noopener noreferrer'
                  target='_blank'
                  href='https://www.weavehelp.com/weavehelp/article/troubleshooting-phone-issues'
                  trackingId={trackingId({
                    context: 'setting',
                    feature: 'device',
                    details: 'provisioning-weave-help-link',
                  })}
                >
                  {t('Need help?')}
                </TextLink>
              </div>
            ),
            accessor: (deviceData) => deviceData,
            cellRenderer: (deviceData: typeof data) => {
              if (!deviceData.provision || !deviceData.deviceId) {
                return <Text size='medium'>{t('No Provisioning Data')}</Text>;
              }
              return (
                <ProvisioningStatus
                  deviceId={deviceData.deviceId}
                  deviceName={deviceData.name ?? 'Unknown Device'}
                  provision={deviceData.provision}
                />
              );
            },
            width: 340,
            omit: device?.type !== DeviceType_Enum.DESK_PHONE,
            disableSortBy: true,
            id: 'provisioning',
          },
        ]}
        data={[data]}
        wrapperStyle={css`
          height: auto;
          padding: ${theme.spacing(2)};
          background: ${theme.colors.white};
          border-radius: ${theme.spacing(1)};
          box-shadow: ${theme.shadows.light};
        `}
      />

      {device?.type !== DeviceType_Enum.MOBILE_APP && (
        // Using a Table here for consistent styling (we want to use the same card style as the table above)
        <Table
          data={[device || {}]}
          colConfig={[
            {
              Header: t('Device Route'),
              disableSortBy: true,
              id: 'deviceRoute',
              cellRenderer: () => (
                <>
                  {device?.callRoute?.id ? (
                    <TextLink
                      onClick={() => {
                        navigate({ to: '/phone/call-routes/:id', params: { id: device.callRoute?.id } });
                      }}
                      trackingId={trackingId({
                        context: 'setting',
                        feature: 'device',
                        details: 'manage-route-link',
                      })}
                    >
                      {t('Manage Route')}
                    </TextLink>
                  ) : (
                    '--'
                  )}
                </>
              ),
              cellAlign: 'center',
              width: 140,
            },
          ]}
          wrapperStyle={css`
            height: 144px;
            padding: ${theme.spacing(2)};
            background: ${theme.colors.white};
            border-radius: ${theme.spacing(1)};
            box-shadow: ${theme.shadows.light};

            @container (max-width: ${CONTAINER_BREAKPOINT}px) {
              height: auto;
              .table-row-group {
                border: none;
              }

              .table-row-group div {
                display: flex;
                justify-content: center;
                align-items: center;
                flex-direction: column;
              }
            }
          `}
        />
      )}
    </div>
  );
};

const WifiAllowedToggle = ({
  device,
  wifiAllowed,
}: {
  device: Device | undefined;
  wifiAllowed: boolean | undefined;
}) => {
  const { t } = useTranslation('phone', { keyPrefix: 'devices' });
  const alerts = useAlert();
  const queryClient = useQueryClient();
  const { modalProps, openModal } = useModalControl();

  const { mutate } = useMutation({
    mutationFn: ({ device }: DevicesTypes.UpdateDeviceType['input']) => DevicesApi.UpdateDevice({ device }),
    onSuccess: (_response, { device: updatedDevice }) => {
      if (device?.deviceId) {
        queryClient.invalidateQueries([device.deviceId, ...DevicesQueryKey.queryKeys.deviceById()]);
      }

      alerts.success(
        updatedDevice?.wifiAllowed
          ? t('Wi-Fi successfully allowed on this device.')
          : t('Wi-Fi successfully disallowed on this device.')
      );
    },
    onError: () => {
      alerts.error(t('Unable to allow Wi-Fi on this device. Please try again.'));
      logError('WifiAllowedToggle:updateDeviceMutation | Failed to update Wi-Fi setting on device', {
        data: {
          deviceId: device?.deviceId,
          deviceModel: device?.model,
        },
      });
    },
  });

  const saveWifiAllowed = (wifiEnabled: boolean) => {
    if (device?.deviceId) {
      mutate({ device: { ...device, wifiAllowed: wifiEnabled } });
    }
  };

  const wifiSwitchProps = useControlledField({
    type: 'switch',
    value: wifiAllowed ?? false,
    onChange: (value) => {
      if (!wifiSwitchProps.active || !device?.deviceId) {
        return;
      }

      if (value) {
        return openModal();
      }

      saveWifiAllowed(false);
    },
  });

  return (
    <>
      <SwitchField
        {...wifiSwitchProps}
        name='device-settings-wifi-allowed'
        label={''}
        trackingId='phone::setting::device-settings:wifi-allowed-toggle'
      />
      <ConfirmationModal
        {...modalProps}
        maxWidth={600}
        title={t('Allow Wi-Fi on This Device?')}
        message={
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${theme.spacing(2)};
            `}
          >
            <Text>
              {t(
                'Weave strongly recommends a wired ethernet connection to guarantee reliable phone performance. If you choose to allow Wi-Fi, you assume the risk of call quality degradation, static, and intermittent connectivity issues during calls.'
              )}
            </Text>
          </div>
        }
        cancelLabel={t('Cancel')}
        confirmLabel={t('Allow')}
        onConfirm={() => saveWifiAllowed(true)}
        confirmTrackingId='phone::setting::device-settings:wifi-allow-confirm-btn'
      />
    </>
  );
};

const ProvisioningStatus = ({
  provision,
  deviceId,
  deviceName,
}: {
  provision: Provision;
  deviceId: string;
  deviceName: string;
}) => {
  const { t } = useTranslation('phone', { keyPrefix: 'devices' });

  const { tooltipProps, triggerProps, Tooltip } = useTooltip();

  const { mutate: unlockProvisioning, isLoading: isUnlocking } = DevicesMutations.useUnlockProvisioning();
  const queryClient = useQueryClient();

  const alert = useAlert();

  const showUnlockButton =
    provision.authentication === ProvisionAuthentication_Enum.UNLOCKED ||
    provision.authentication === ProvisionAuthentication_Enum.LOCKED;

  const handleUnlockProvisioning = () => {
    unlockProvisioning(
      { deviceIds: [deviceId] },
      {
        onSuccess: (resp) => {
          alert.success(
            t(
              '{{deviceName}} provisioning successfully unlocked. Please reboot your phone to complete its provisioning.',
              { deviceName }
            )
          );

          // Update the query cache directly instead of invalidating
          const queryKey = [deviceId, ...DevicesQueryKey.queryKeys.deviceById()];
          queryClient.setQueryData(queryKey, (oldData: any) => {
            if (!oldData?.device) return oldData;

            const unlockedDevice = resp.unlockedDevices?.[0];
            const newAuthenticationEnforcedAt =
              unlockedDevice?.provisionAuthenticationEnforcedAt || oldData.device.provision?.authenticationEnforcedAt;

            return {
              ...oldData,
              device: {
                ...oldData.device,
                provision: {
                  ...oldData.device.provision,
                  authentication: ProvisionAuthentication_Enum.UNLOCKED,
                  authenticationEnforcedAt: newAuthenticationEnforcedAt,
                },
              },
            };
          });
        },
        onError: (error) => {
          console.error('Failed to unlock provisioning:', error);
          alert.error(t('Failed to unlock provisioning.'));
          logError('ProvisioningSection:unlockProvisioningMutation | Failed to unlock provisioning', {
            data: { deviceId, error },
          });
        },
      }
    );
  };

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: ${theme.spacing(1)};
      `}
    >
      <div {...triggerProps}>
        <ChipOptional
          leftElement={
            <Icon name={provision.health === ProvisionHealth_Enum.PROVISIONING ? 'check-small' : 'x-small'} size={14} />
          }
          variant={provision.health === ProvisionHealth_Enum.PROVISIONING ? 'success' : 'critical'}
        >
          {t('Provisioning')}
        </ChipOptional>
      </div>

      <Tooltip
        {...tooltipProps}
        css={css`
          width: max-content;
          max-width: 224px;
        `}
      >
        <Text size='medium' color='white'>
          {!!provision.provisionedAt
            ? t('Last Provisioned on {{provisionedAt}}', {
                provisionedAt: formatDate(provision.provisionedAt, 'MMM D, YYYY; hh:mm A z'),
              })
            : t('Unknown provisioned date')}
        </Text>
        {provision.authentication !== ProvisionAuthentication_Enum.DISABLED && (
          <Text size='medium' color='white'>
            {provision.authentication === ProvisionAuthentication_Enum.LOCKED
              ? t('Secured provisioning')
              : provision.authentication === ProvisionAuthentication_Enum.UNLOCKED
              ? t('Unlocked provisioning until {{expiresAt}}', {
                  expiresAt: formatDate(provision.authenticationEnforcedAt, 'MMM D, YYYY; hh:mm A z'),
                })
              : t('Authentication status unknown.')}
          </Text>
        )}
      </Tooltip>

      {showUnlockButton && (
        <Button
          variant='secondary'
          iconName={provision.authentication === ProvisionAuthentication_Enum.UNLOCKED ? 'unlock' : 'lock'}
          loading={isUnlocking}
          onClick={handleUnlockProvisioning}
          trackingId={trackingId({
            context: 'setting',
            feature: 'device',
            details: 'unlock-provisioning-btn',
          })}
        >
          <Text
            size='medium'
            as='span'
            css={css`
              line-height: normal;
            `}
          >
            {t('Unlock Provisioning')}
          </Text>
        </Button>
      )}
    </div>
  );
};
